import { PlusOutlined } from "@ant-design/icons";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Checkbox, Form, Input, Select, Table } from "antd";
import { descriptionRules } from "common/FormValidationRules";
import { actionLabel, offeredOtherActions, offersOnlyOtherActions } from "common/permissionActions";
import { FullHeightContainerLayout } from "components";
import { useState } from "react";
import _ from "lodash";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;
const { Option } = Select;

const RolePermissionList = ({ formRef, permissionMasterData, permissions, onPermissionChange, readOnly, onDeletePermission, getFormErrors, add, remove }) => {
    const [scrollToLastRow, setScrollToLastRow] = useState(false);

    const masterPermissionOf = (permissionName) => (permissionName ? permissionMasterData.find((item) => item.name === permissionName) : undefined);

    const rowMasterPermission = (name) => (formRef && formRef.current ? masterPermissionOf(formRef.current.getFieldValue(["permissions", name, "name"])) : undefined);

    // Add, Edit and Delete are off for a view-only permission; all four are off for a permission
    // that offers only its own actions (shown under Other actions).
    const isPermissionReadOnly = (name, actionProp) => {
        const masterPermission = rowMasterPermission(name);
        if (!masterPermission) {
            return false;
        }
        if (offersOnlyOtherActions(masterPermission)) {
            return true;
        }
        return actionProp !== "canView" && masterPermission.actions.length === 1 && masterPermission.actions.includes("view");
    };

    return (
        <FullHeightContainerLayout
            showHeader={false}
            showFooter={true}
            content={
                <Table
                    size="small"
                    dataSource={permissions}
                    rowKey="key"
                    pagination={false}
                    scroll={{ y: "100vh" }}
                    className="form-table container-height-100"
                    data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_TABLE}
                >
                    <Column width="5%" title="No." key="index" render={(value, field, index) => index + 1} />
                    <Column
                        title={"Permission"}
                        render={(value, field, index) => {
                            return (
                                <>
                                    <Form.Item {...field} name={[field.name, "name"]} {...getFormErrors(`permissions.${field.name}.name`)}>
                                        <Select
                                            data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_PERMISSION_NAME_SELECT}
                                            showSearch
                                            placeholder="Search to Select"
                                            optionFilterProp="children"
                                            filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                            onChange={(value) => onPermissionChange(value, field)}
                                            {...(() => (readOnly ? { open: false } : {}))()}
                                        >
                                            {[...permissionMasterData]
                                                .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
                                                .map((permission) => (
                                                    <Option key={permission.name} value={permission.name}>
                                                        {permission.name}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </Form.Item>
                                    {/* ManageRole matches a submitted row to the one the role loaded with by permissionId. It has
                                        to be a registered field to come back from the form; a row added in the form has none. */}
                                    <Form.Item {...field} name={[field.name, "permissionId"]} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={[field.name, "resourceName"]} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={[field.name, "resourceId"]} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={[field.name, "effect"]} hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={[field.name, "condition"]} hidden>
                                        <Input />
                                    </Form.Item>
                                </>
                            );
                        }}
                    />
                    <Column
                        title={"Description"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "description"]} {...getFormErrors(`permissions.${field.name}.description`)} rules={descriptionRules}>
                                    <Input readOnly={readOnly} data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_PERMISSION_DESCRIPTION_INPUT}></Input>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="6%"
                        title={"Active"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "active"]} valuePropName="checked" initialValue={true}>
                                    <Checkbox disabled={readOnly} data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_ACTIVE_CHECK}></Checkbox>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="6%"
                        title={"Add"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "canAdd"]} valuePropName="checked" dependencies={[field.name, "name"]} initialValue={true}>
                                    <Checkbox
                                        disabled={isPermissionReadOnly(field.name, "canAdd") || readOnly}
                                        data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_CAN_ADD_CHECK}
                                    ></Checkbox>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="6%"
                        title={"View"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "canView"]} valuePropName="checked" dependencies={[field.name, "name"]} initialValue={true}>
                                    <Checkbox
                                        disabled={isPermissionReadOnly(field.name, "canView") || readOnly}
                                        data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_CAN_VIEW_CHECK}
                                    ></Checkbox>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="6%"
                        title={"Edit"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "canEdit"]} valuePropName="checked" dependencies={[field.name, "name"]} initialValue={true}>
                                    <Checkbox
                                        disabled={isPermissionReadOnly(field.name, "canEdit") || readOnly}
                                        data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_CAN_EDIT_CHECK}
                                    ></Checkbox>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="6%"
                        title={"Delete"}
                        render={(value, field, index) => {
                            return (
                                <Form.Item {...field} name={[field.name, "canDelete"]} valuePropName="checked" dependencies={[field.name, "name"]} initialValue={true}>
                                    <Checkbox
                                        disabled={isPermissionReadOnly(field.name, "canDelete") || readOnly}
                                        data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_CAN_DELETE_CHECK}
                                    ></Checkbox>
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        title={"Other actions"}
                        render={(value, field, index) => {
                            // The permission's own actions (e.g. Review), plus any the role held or
                            // holds, so saving never drops one. Re-rendered only when this row's
                            // permission or actions change.
                            const rowState = (values) => _.pick(_.get(values, ["permissions", field.name]) || {}, ["name", "otherActions", "heldOtherActions"]);
                            return (
                                <Form.Item noStyle shouldUpdate={(previous, current) => !_.isEqual(rowState(previous), rowState(current))}>
                                    {({ getFieldValue }) => {
                                        const options = offeredOtherActions(
                                            masterPermissionOf(getFieldValue(["permissions", field.name, "name"])),
                                            getFieldValue(["permissions", field.name, "otherActions"]),
                                            getFieldValue(["permissions", field.name, "heldOtherActions"])
                                        ).map((action) => ({ label: actionLabel(action), value: action }));
                                        return (
                                            <Form.Item {...field} name={[field.name, "otherActions"]} initialValue={[]}>
                                                <Checkbox.Group
                                                    options={options}
                                                    disabled={readOnly}
                                                    data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_OTHER_ACTIONS_CHECK_GROUP}
                                                />
                                            </Form.Item>
                                        );
                                    }}
                                </Form.Item>
                            );
                        }}
                    />
                    <Column
                        width="5%"
                        render={(value, field, index) => {
                            return (
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className={!readOnly ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    onClick={() => {
                                        if (!readOnly) onDeletePermission(field, () => remove(field.name));
                                    }}
                                    data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_PERMISSION_DELETE_BUTTON}
                                />
                            );
                        }}
                    />
                </Table>
            }
            footer={
                <Form.Item>
                    <Button
                        block
                        onClick={() => {
                            setScrollToLastRow(true);
                            add();
                        }}
                        disabled={readOnly}
                        data-testid={CypressTestIds.ROLES_PERMISSIONS_LIST_ADD_PERMISSION_BUTTON}
                    >
                        <PlusOutlined />
                        {permissions.length === 0 ? "Add permission" : "Add another permission"}
                    </Button>
                </Form.Item>
            }
        ></FullHeightContainerLayout>
    );
};

export default RolePermissionList;
